import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import { groupAdminUserRows } from "../lib/adminUsers";
import type {
  AdminErrorClusterDetails,
  AdminErrorScanResult,
  AdminErrorSummary,
  AdminOverview,
  AdminUserRow,
  ErrorCluster
} from "../types/api";

const USERS_PAGE_SIZE = 20;

function money(value: number): string {
  return `$${value.toFixed(2)}`;
}

function dateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function displayEmail(row: AdminUserRow): string {
  return row.user.email || row.user.loginEmail || "—";
}

function searchBlob(row: AdminUserRow): string {
  return [
    row.user.id,
    row.user.email,
    row.user.loginEmail,
    row.user.name,
    row.account?.id,
    row.account?.name,
    row.billing.sub2apiKeyId,
    row.billing.keyPreview,
    row.agent?.id,
    row.agent?.status,
    row.agent?.runtime?.containerName,
    row.agent?.runtime?.instanceName,
    row.agent?.runtime?.status
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function statusClass(status: string | null | undefined): string {
  if (!status) return "";
  if (status === "ready" || status === "online" || status === "completed" || status === "succeeded" || status === "verified") return "good";
  if (status === "failed" || status === "error" || status === "revoked" || status === "critical" || status === "blocked") return "bad";
  if (
    status === "running" ||
    status === "queued" ||
    status === "provisioning" ||
    status === "high" ||
    status === "needs_review" ||
    status === "ready_for_agent"
  ) return "warn";
  return "";
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="admin-metric">
      <span className="admin-label">{label}</span>
      <strong>{value}</strong>
      {hint ? <span className="admin-hint">{hint}</span> : null}
    </div>
  );
}

function Kv({ label, value, mono = false }: { label: string; value: string | number | null; mono?: boolean }) {
  return (
    <div className="admin-kv">
      <span className="admin-label">{label}</span>
      <span className={mono ? "admin-value admin-mono" : "admin-value"}>{value ?? "—"}</span>
    </div>
  );
}

function UserRow({
  row,
  workspaceRows,
  onAddBalance
}: {
  row: AdminUserRow;
  workspaceRows: AdminUserRow[];
  onAddBalance: (row: AdminUserRow, amountUsd: number) => Promise<void>;
}) {
  const { lang } = useI18n();
  const zh = lang === "zh";
  const [balanceDraft, setBalanceDraft] = useState("");
  const [balanceSaving, setBalanceSaving] = useState(false);
  const [balanceMessage, setBalanceMessage] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const label = {
    noAccount: zh ? "无账号" : "no account",
    account: zh ? "账号" : "Account",
    user: zh ? "用户" : "User",
    userId: zh ? "用户 ID" : "User ID",
    accountId: zh ? "账号 ID" : "Account ID",
    workspaces: zh ? "归属工作区" : "Workspaces",
    planRole: zh ? "套餐 / 角色" : "Plan / role",
    created: zh ? "创建时间" : "Created",
    billing: zh ? "费用" : "Billing",
    apiKey: zh ? "API Key" : "API Key",
    sub2apiId: zh ? "Sub2API ID" : "Sub2API ID",
    paid: zh ? "已支付" : "Paid",
    addBalanceAmount: zh ? "增加多少余额" : "Balance to add",
    addBalance: zh ? "增加余额" : "Add balance",
    balanceAdded: zh ? "余额已增加" : "Balance added",
    noBillingKey: zh ? "暂无计费 Key，无法增加余额" : "No billing key; balance cannot be added",
    invalidBalance: zh ? "请输入大于 0 的金额" : "Enter an amount greater than 0",
    balance: zh ? "余额" : "Balance",
    usage: zh ? "使用情况" : "Usage",
    sessions: zh ? "会话" : "Sessions",
    total: zh ? "总计" : "total",
    completed: zh ? "已完成" : "Completed",
    runningQueued: zh ? "运行 / 排队" : "Running / queued",
    failedCancelled: zh ? "失败 / 取消" : "Failed / cancelled",
    lastSession: zh ? "最近任务" : "Last session",
    runtime: zh ? "运行环境" : "Runtime",
    agentId: zh ? "智能体 ID" : "Agent ID",
    container: zh ? "容器" : "Container",
    ports: zh ? "端口" : "Ports",
    devices: zh ? "设备" : "Devices",
    online: zh ? "在线" : "online",
    lastError: zh ? "最近错误" : "Last error"
  };
  const agentStatus = row.agent?.status ?? "—";
  const runtimeStatus = row.agent?.runtime?.status ?? "—";
  const canAddBalance = row.billing.hasKey && row.billing.sub2apiKeyId != null;
  const trimmedBalanceDraft = balanceDraft.trim();
  const draftAmountUsd = Number(trimmedBalanceDraft);
  const roundedDraftAmountUsd = Number.isFinite(draftAmountUsd) ? Math.round(draftAmountUsd * 100) / 100 : NaN;
  const canSubmitBalance = trimmedBalanceDraft.length > 0 && Number.isFinite(roundedDraftAmountUsd) && roundedDraftAmountUsd > 0;

  async function addBalance() {
    if (!trimmedBalanceDraft) {
      setBalanceError(label.invalidBalance);
      setBalanceMessage(null);
      return;
    }
    if (!canSubmitBalance) {
      setBalanceError(label.invalidBalance);
      setBalanceMessage(null);
      return;
    }
    setBalanceSaving(true);
    setBalanceError(null);
    setBalanceMessage(null);
    try {
      await onAddBalance(row, roundedDraftAmountUsd);
      setBalanceDraft("");
      setBalanceMessage(label.balanceAdded);
    } catch (error) {
      setBalanceError(error instanceof Error ? error.message : "Failed");
    } finally {
      setBalanceSaving(false);
    }
  }

  return (
    <article className="admin-user-row">
      <div className="admin-user-head">
        <div className="admin-user-title">
          <strong>{displayEmail(row)}</strong>
          <span>{row.user.name || row.account?.name || row.user.id}</span>
        </div>
        <div className="admin-status-group">
          {row.account ? <span className="admin-pill">{row.account.agentMode}</span> : <span className="admin-pill muted-pill">{label.noAccount}</span>}
          <span className={`admin-pill ${statusClass(agentStatus)}`}>{agentStatus}</span>
        </div>
      </div>

      <div className="admin-grid">
        <section className="admin-cell">
          <h3>{row.account ? label.account : label.user}</h3>
          <Kv label={label.userId} value={row.user.id} mono />
          <Kv label={label.accountId} value={row.account?.id ?? null} mono />
          <Kv label={label.planRole} value={row.account ? `${row.account.plan} / ${row.account.role}` : "—"} />
          <Kv label={label.created} value={dateTime(row.user.createdAt)} />
        </section>

        <section className="admin-cell">
          <h3>{label.billing}</h3>
          <Kv label={label.apiKey} value={row.billing.keyPreview ?? "—"} mono />
          <Kv label={label.sub2apiId} value={row.billing.sub2apiKeyId ?? "—"} mono />
          <Kv label={label.paid} value={money(row.payments.paidUsd)} />
          <Kv label={label.balance} value={money(row.billing.remainingUsd)} />
          <div className="admin-quota-edit">
            <label className="admin-label" htmlFor={`balance-${row.user.id}`}>
              {label.addBalanceAmount}
            </label>
            <div className="admin-quota-controls">
              <input
                id={`balance-${row.user.id}`}
                className="input admin-quota-input"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={balanceDraft}
                disabled={!canAddBalance || balanceSaving}
                onChange={(event) => {
                  setBalanceDraft(event.target.value);
                  setBalanceError(null);
                  setBalanceMessage(null);
                }}
              />
              <button
                type="button"
                className="btn-secondary admin-quota-button"
                disabled={!canAddBalance || balanceSaving || !canSubmitBalance}
                onClick={() => void addBalance()}
              >
                {balanceSaving ? <span className="spinner inline" /> : label.addBalance}
              </button>
            </div>
            {!canAddBalance ? <span className="admin-hint">{label.noBillingKey}</span> : null}
            {balanceMessage ? <span className="admin-quota-message">{balanceMessage}</span> : null}
            {balanceError ? <span className="admin-quota-error">{balanceError}</span> : null}
          </div>
        </section>

        <section className="admin-cell">
          <h3>{label.usage}</h3>
          <Kv label={label.sessions} value={`${row.usage.sessionsTotal} ${label.total}`} />
          <Kv label={label.completed} value={row.usage.completed} />
          <Kv label={label.runningQueued} value={`${row.usage.running} / ${row.usage.queued}`} />
          <Kv label={label.failedCancelled} value={`${row.usage.failed} / ${row.usage.cancelled}`} />
          <Kv label={label.lastSession} value={dateTime(row.usage.lastSessionAt)} />
        </section>

        <section className="admin-cell">
          <h3>{label.runtime}</h3>
          <Kv label={label.agentId} value={row.agent?.id ?? null} mono />
          <Kv label={label.runtime} value={runtimeStatus} />
          <Kv label={label.container} value={row.agent?.runtime?.containerName ?? null} mono />
          <Kv label={label.ports} value={row.agent?.runtime ? `${row.agent.runtime.apiPort} / ${row.agent.runtime.dashboardPort}` : "—"} />
          <Kv label={label.devices} value={`${row.devices.online}/${row.devices.total} ${label.online}`} />
          {row.agent?.runtime?.lastError ? <Kv label={label.lastError} value={row.agent.runtime.lastError} /> : null}
        </section>
      </div>

      {workspaceRows.length ? (
        <section className="admin-workspace-memberships">
          <h3>{label.workspaces}</h3>
          <div className="admin-workspace-list">
            {workspaceRows.map((workspaceRow) => (
              <div className="admin-workspace-membership" key={workspaceRow.account!.id}>
                <div className="admin-workspace-identity">
                  <strong>{workspaceRow.account!.name}</strong>
                  <span className="admin-mono">{workspaceRow.account!.id}</span>
                </div>
                <div className="admin-status-group">
                  <span className="admin-pill">{workspaceRow.account!.role}</span>
                  <span className="admin-pill">{workspaceRow.account!.agentMode}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}

function statusCount(summary: AdminErrorSummary | null, status: string): number {
  return summary?.byStatus.find((item) => item.status === status)?.count ?? 0;
}

function kindIncidents(summary: AdminErrorSummary | null, kind: string): number {
  return summary?.byKind.find((item) => item.kind === kind)?.incidents ?? 0;
}

function clusterSearchBlob(cluster: ErrorCluster): string {
  return [
    cluster.fingerprint,
    cluster.kind,
    cluster.severity,
    cluster.title,
    cluster.status,
    cluster.summary,
    cluster.sampleSessionId,
    cluster.validationStatus,
    cluster.autofixStatus
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function scanSummary(result: AdminErrorScanResult | null, zh: boolean): string {
  if (!result) return "";
  const mode = result.dryRun ? (zh ? "回测" : "Backtest") : zh ? "扫描" : "Scan";
  return `${mode}: ${result.scanned} ${zh ? "条任务" : "sessions"}, ${result.clustersTouched} clusters`;
}

function EvidencePreview({ value }: { value: unknown }) {
  return (
    <details className="admin-error-evidence">
      <summary>Evidence</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function ErrorClusterRow({
  cluster,
  selected,
  onSelect
}: {
  cluster: ErrorCluster;
  selected: boolean;
  onSelect: (fingerprint: string) => void;
}) {
  return (
    <button
      type="button"
      className={`admin-error-row ${selected ? "selected" : ""}`}
      onClick={() => onSelect(cluster.fingerprint)}
    >
      <div className="admin-error-row-main">
        <strong>{cluster.title}</strong>
        <span className="admin-mono">{cluster.fingerprint}</span>
      </div>
      <div className="admin-error-row-meta">
        <span className={`admin-pill ${statusClass(cluster.severity)}`}>{cluster.severity}</span>
        <span className="admin-pill">{cluster.kind}</span>
        <span>{cluster.incidentCount} incidents</span>
        <span>{dateTime(cluster.lastSeenAt)}</span>
      </div>
    </button>
  );
}

function AdminErrorsPanel({ refreshSignal }: { refreshSignal: number }) {
  const { client } = useAuth();
  const { lang } = useI18n();
  const zh = lang === "zh";
  const [summary, setSummary] = useState<AdminErrorSummary | null>(null);
  const [clusters, setClusters] = useState<ErrorCluster[]>([]);
  const [query, setQuery] = useState("");
  const [selectedFingerprint, setSelectedFingerprint] = useState<string | null>(null);
  const [details, setDetails] = useState<AdminErrorClusterDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<AdminErrorScanResult | null>(null);

  const label = {
    refresh: zh ? "刷新错误" : "Refresh errors",
    backtest: zh ? "回测" : "Backtest",
    scan: zh ? "立即扫描" : "Scan now",
    search: zh ? "搜索错误、fingerprint、session" : "Search errors, fingerprint, session",
    openClusters: zh ? "打开的错误" : "Open clusters",
    incidents: zh ? "错误任务" : "Incidents",
    high: zh ? "高风险" : "High",
    unknown: zh ? "未知" : "Unknown",
    stale: zh ? "卡住" : "Stale",
    clusterList: zh ? "错误聚类" : "Error clusters",
    detail: zh ? "详情" : "Details",
    noClusters: zh ? "暂无错误聚类" : "No error clusters",
    noSelection: zh ? "选择一个错误查看详情" : "Select an error to inspect",
    validate: zh ? "验证" : "Validate",
    bundle: zh ? "生成诊断包" : "Bug bundle",
    autofix: zh ? "请求修复" : "Request autofix",
    validation: zh ? "验证状态" : "Validation",
    autofixStatus: zh ? "修复状态" : "Autofix",
    recentIncidents: zh ? "最近任务" : "Recent incidents",
    runs: zh ? "修复记录" : "Autofix runs",
    lastSeen: zh ? "最近出现" : "Last seen",
    firstSeen: zh ? "首次出现" : "First seen",
    session: zh ? "任务" : "Session",
    runtime: zh ? "运行环境" : "Runtime",
    prompt: zh ? "提示预览" : "Prompt",
    error: zh ? "错误" : "Error"
  };

  const loadDetails = useCallback(
    async (fingerprint: string) => {
      if (!client) return;
      setDetailsLoading(true);
      setError(null);
      try {
        setDetails(await client.adminErrorCluster(fingerprint));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load error details");
      } finally {
        setDetailsLoading(false);
      }
    },
    [client]
  );

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    try {
      const [nextSummary, clusterResponse] = await Promise.all([
        client.adminErrorSummary(),
        client.adminErrorClusters({ limit: 200 })
      ]);
      setSummary(nextSummary);
      setClusters(clusterResponse.clusters);
      setSelectedFingerprint((current) => {
        const nextSelected =
          current && clusterResponse.clusters.some((cluster) => cluster.fingerprint === current)
            ? current
            : clusterResponse.clusters[0]?.fingerprint ?? null;
        if (nextSelected) void loadDetails(nextSelected);
        else setDetails(null);
        return nextSelected;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load errors");
    } finally {
      setLoading(false);
    }
  }, [client, loadDetails]);

  useEffect(() => {
    void load();
  }, [load, refreshSignal]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clusters;
    return clusters.filter((cluster) => clusterSearchBlob(cluster).includes(q));
  }, [clusters, query]);

  const selectCluster = useCallback(
    (fingerprint: string) => {
      setSelectedFingerprint(fingerprint);
      setActionMessage(null);
      void loadDetails(fingerprint);
    },
    [loadDetails]
  );

  const runBacktest = useCallback(async () => {
    if (!client) return;
    setActionLoading("backtest");
    setActionMessage(null);
    setError(null);
    try {
      const result = await client.adminErrorBacktest({ limit: 500 });
      setLastResult(result);
      setActionMessage(scanSummary(result, zh));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Backtest failed");
    } finally {
      setActionLoading(null);
    }
  }, [client, zh]);

  const runScan = useCallback(async () => {
    if (!client) return;
    setActionLoading("scan");
    setActionMessage(null);
    setError(null);
    try {
      const result = await client.adminErrorScan({ limit: 200 });
      setLastResult(result);
      setActionMessage(scanSummary(result, zh));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scan failed");
    } finally {
      setActionLoading(null);
    }
  }, [client, load, zh]);

  const runClusterAction = useCallback(
    async (kind: "validate" | "bundle" | "autofix") => {
      if (!client || !selectedFingerprint) return;
      setActionLoading(kind);
      setActionMessage(null);
      setError(null);
      try {
        if (kind === "validate") {
          const result = await client.adminErrorValidate(selectedFingerprint);
          setActionMessage(`${label.validation}: ${result.validation.verdict}`);
        } else if (kind === "bundle") {
          const result = await client.adminErrorBugBundle(selectedFingerprint);
          setActionMessage(result.path);
        } else {
          const result = await client.adminErrorAutofix(selectedFingerprint);
          setActionMessage(`${label.autofixStatus}: ${result.run.status}`);
        }
        await loadDetails(selectedFingerprint);
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Action failed");
      } finally {
        setActionLoading(null);
      }
    },
    [client, label.autofixStatus, label.validation, load, loadDetails, selectedFingerprint]
  );

  return (
    <section className="admin-errors">
      <div className="admin-error-actions">
        <input
          className="input admin-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={label.search}
        />
        <button type="button" className="btn-secondary" onClick={() => void load()} disabled={loading}>
          {loading ? <span className="spinner inline" /> : label.refresh}
        </button>
        <button type="button" className="btn-secondary" onClick={() => void runBacktest()} disabled={Boolean(actionLoading)}>
          {actionLoading === "backtest" ? <span className="spinner inline" /> : label.backtest}
        </button>
        <button type="button" className="btn-primary" onClick={() => void runScan()} disabled={Boolean(actionLoading)}>
          {actionLoading === "scan" ? <span className="spinner inline" /> : label.scan}
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {actionMessage ? <div className="admin-action-message">{actionMessage}</div> : null}

      <div className="admin-summary-grid">
        <Metric label={label.openClusters} value={String(statusCount(summary, "open"))} hint={`${summary?.totals.clusters ?? 0} total`} />
        <Metric label={label.incidents} value={String(summary?.totals.incidents ?? "—")} />
        <Metric label={label.high} value={String(summary?.totals.high ?? "—")} hint={`${summary?.totals.critical ?? 0} critical`} />
        <Metric label={label.unknown} value={String(kindIncidents(summary, "unknown"))} />
        <Metric label={label.stale} value={String(kindIncidents(summary, "stale_running"))} />
      </div>

      {lastResult ? (
        <div className="admin-scan-result">
          <span>{scanSummary(lastResult, zh)}</span>
          <span>{dateTime(lastResult.untilIso)}</span>
        </div>
      ) : null}

      <div className="admin-error-layout">
        <section className="admin-error-list" aria-label={label.clusterList}>
          <div className="admin-section-title">
            <h2>{label.clusterList}</h2>
            <span>{filtered.length} / {clusters.length}</span>
          </div>
          {filtered.length ? (
            filtered.map((cluster) => (
              <ErrorClusterRow
                key={cluster.fingerprint}
                cluster={cluster}
                selected={cluster.fingerprint === selectedFingerprint}
                onSelect={selectCluster}
              />
            ))
          ) : (
            <div className="admin-empty">{label.noClusters}</div>
          )}
        </section>

        <section className="admin-error-detail" aria-label={label.detail}>
          <div className="admin-section-title">
            <h2>{label.detail}</h2>
            {detailsLoading ? <span className="spinner inline" /> : null}
          </div>
          {!details ? (
            <div className="admin-empty">{label.noSelection}</div>
          ) : (
            <>
              <div className="admin-error-detail-head">
                <div>
                  <strong>{details.cluster.title}</strong>
                  <span className="admin-mono">{details.cluster.fingerprint}</span>
                </div>
                <div className="admin-status-group">
                  <span className={`admin-pill ${statusClass(details.cluster.severity)}`}>{details.cluster.severity}</span>
                  <span className="admin-pill">{details.cluster.kind}</span>
                  <span className="admin-pill">{details.cluster.status}</span>
                </div>
              </div>

              <div className="admin-grid compact">
                <section className="admin-cell">
                  <h3>{label.validation}</h3>
                  <Kv label={label.validation} value={details.cluster.validationStatus} />
                  <Kv label={label.lastSeen} value={dateTime(details.cluster.lastSeenAt)} />
                  <Kv label={label.firstSeen} value={dateTime(details.cluster.firstSeenAt)} />
                </section>
                <section className="admin-cell">
                  <h3>{label.autofixStatus}</h3>
                  <Kv label={label.autofixStatus} value={details.cluster.autofixStatus} />
                  <Kv label="Run" value={details.cluster.lastAutofixRunId} mono />
                  <Kv label="Sample" value={details.cluster.sampleSessionId} mono />
                </section>
              </div>

              <div className="admin-error-button-row">
                <button type="button" className="btn-secondary" onClick={() => void runClusterAction("validate")} disabled={Boolean(actionLoading)}>
                  {actionLoading === "validate" ? <span className="spinner inline" /> : label.validate}
                </button>
                <button type="button" className="btn-secondary" onClick={() => void runClusterAction("bundle")} disabled={Boolean(actionLoading)}>
                  {actionLoading === "bundle" ? <span className="spinner inline" /> : label.bundle}
                </button>
                <button type="button" className="btn-primary" onClick={() => void runClusterAction("autofix")} disabled={Boolean(actionLoading)}>
                  {actionLoading === "autofix" ? <span className="spinner inline" /> : label.autofix}
                </button>
              </div>

              <div className="admin-error-section">
                <h3>{label.recentIncidents}</h3>
                {details.incidents.map((incident) => (
                  <article className="admin-error-incident" key={incident.id}>
                    <div className="admin-error-incident-head">
                      <span className="admin-mono">{incident.sessionId}</span>
                      <span>{dateTime(incident.detectedAt)}</span>
                    </div>
                    <Kv label={label.session} value={incident.sessionStatus} />
                    <Kv label={label.runtime} value={incident.runtimeStatus ?? "—"} />
                    <Kv label={label.prompt} value={incident.promptPreview ?? "—"} />
                    <Kv label={label.error} value={incident.errorMessage ?? incident.summary} />
                    <EvidencePreview value={incident.evidence} />
                  </article>
                ))}
              </div>

              {details.autofixRuns.length ? (
                <div className="admin-error-section">
                  <h3>{label.runs}</h3>
                  {details.autofixRuns.map((run) => (
                    <div className="admin-error-run" key={run.id}>
                      <span className={`admin-pill ${statusClass(run.status)}`}>{run.status}</span>
                      <span className="admin-mono">{run.id}</span>
                      <span>{run.summary ?? run.bugBundlePath ?? dateTime(run.createdAt)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </section>
  );
}

export function AdminScreen() {
  const { client } = useAuth();
  const { t, lang } = useI18n();
  const zh = lang === "zh";
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"users" | "errors">("users");
  const [errorsRefreshSignal, setErrorsRefreshSignal] = useState(0);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    try {
      setOverview(await client.adminOverview());
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [client, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => groupAdminUserRows(overview?.users ?? []), [overview]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => group.rows.some((row) => searchBlob(row).includes(q)));
  }, [groups, query]);

  // Paginate the (filtered) list; reset to page 0 whenever the result set changes.
  const pageCount = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(clampedPage * USERS_PAGE_SIZE, clampedPage * USERS_PAGE_SIZE + USERS_PAGE_SIZE);
  useEffect(() => {
    setPage(0);
  }, [query, overview]);

  const summary = overview?.summary;

  const addBalance = useCallback(
    async (row: AdminUserRow, amountUsd: number) => {
      if (!client) throw new Error("client unavailable");
      await client.adminAddUserBalance(row.user.id, amountUsd);
      await load();
    },
    [client, load]
  );

  return (
    <main className="admin-main">
      <div className="admin-toolbar">
        <div>
          <h1>{t("admin.title")}</h1>
          <p>{t("admin.subtitle")}</p>
        </div>
        {activeTab === "users" ? (
          <button type="button" className="btn-secondary" onClick={() => void load()} disabled={loading}>
            {loading ? <span className="spinner inline" /> : t("admin.refresh")}
          </button>
        ) : (
          <button type="button" className="btn-secondary" onClick={() => setErrorsRefreshSignal((value) => value + 1)}>
            {t("admin.refresh")}
          </button>
        )}
      </div>

      <div className="admin-tabs" role="tablist" aria-label={t("admin.title")}>
        <button type="button" className={activeTab === "users" ? "active" : ""} onClick={() => setActiveTab("users")}>
          {zh ? "用户" : "Users"}
        </button>
        <button type="button" className={activeTab === "errors" ? "active" : ""} onClick={() => setActiveTab("errors")}>
          {zh ? "错误" : "Errors"}
        </button>
      </div>

      {activeTab === "users" ? (
        <>
          {error ? <div className="error-banner">{error}</div> : null}

          <div className="admin-summary-grid">
            <Metric label={t("admin.users")} value={String(summary?.userCount ?? "—")} hint={`${summary?.accountCount ?? 0} ${t("admin.accounts")}`} />
            <Metric
              label={t("admin.agents")}
              value={`${summary?.readyAgentCount ?? 0}/${summary?.cloudAgentCount ?? 0}`}
              hint={t("admin.ready")}
            />
            <Metric
              label={t("admin.sessions")}
              value={String(summary?.sessionsTotal ?? "—")}
              hint={`${summary?.sessionsRunning ?? 0} ${t("admin.running")} · ${summary?.sessionsFailed ?? 0} ${t("admin.failed")}`}
            />
            <Metric
              label={t("admin.quota")}
              value={summary ? money(summary.remainingUsd) : "—"}
            />
            <Metric label={t("admin.paid")} value={summary ? money(summary.paidUsd) : "—"} />
          </div>

          <div className="admin-list-head">
            <input
              className="input admin-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("admin.search")}
            />
            {filtered.length > 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={clampedPage === 0}
                  onClick={() => setPage(clampedPage - 1)}
                  aria-label={zh ? "上一页" : "Previous page"}
                >
                  ‹
                </button>
                <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {clampedPage * USERS_PAGE_SIZE + 1}–{Math.min(filtered.length, (clampedPage + 1) * USERS_PAGE_SIZE)} / {filtered.length}
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={clampedPage >= pageCount - 1}
                  onClick={() => setPage(clampedPage + 1)}
                  aria-label={zh ? "下一页" : "Next page"}
                >
                  ›
                </button>
              </div>
            ) : overview ? (
              <span>0 / {groups.length}</span>
            ) : null}
          </div>

          <div className="admin-list">
            {!overview && loading ? (
              <div className="center admin-loading"><span className="spinner" /></div>
            ) : paged.length ? (
              paged.map((group) => (
                <UserRow
                  key={group.userId}
                  row={group.primary}
                  workspaceRows={group.workspaceRows}
                  onAddBalance={addBalance}
                />
              ))
            ) : (
              <div className="admin-empty">{t("admin.noUsers")}</div>
            )}
          </div>
        </>
      ) : (
        <AdminErrorsPanel refreshSignal={errorsRefreshSignal} />
      )}
    </main>
  );
}
